<script lang="ts">
  import { nowHHMM } from '../lib/format';
  import { isClockTime, type TimeStore } from '../stores/time.svelte';

  interface Properties {
    store: TimeStore;
    /** Injectable clock — production defaults to `nowHHMM`; tests stub for determinism. */
    now?: () => string;
  }

  const { store, now: computeNow = nowHHMM }: Properties = $props();

  /** Hours a day offers. */
  const HOURS_PER_DAY = 24;
  /** Minutes an hour offers. */
  const MINUTES_PER_HOUR = 60;
  /** Two-digit label for a clock component. */
  function padded(component: number): string {
    return String(component).padStart(2, '0');
  }

  /**
   * Fills a `<select>` with two-digit options `00`…`length - 1`. The list never changes,
   * so it is built once here rather than as an `{#each}`, which would make 84 reactive
   * blocks for the two fields.
   */
  function clockOptions(select: HTMLSelectElement, length: number): void {
    for (let value = 0; value < length; value += 1) {
      const option = document.createElement('option');
      option.textContent = padded(value);
      option.value = padded(value);
      select.append(option);
    }
  }

  /** Re-computed on each tick so 'now' mode tracks the wall clock. */
  // svelte-ignore state_referenced_locally
  const clock = $state({ value: computeNow() });

  $effect(() => {
    const intervalId = setInterval(() => {
      clock.value = computeNow();
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  });

  const isLive = $derived(store.mode === 'now');

  /** `candidate` when the dropdowns have options for it, else `fallback`. */
  function clockTimeOrFallback(candidate: string, fallback: string): string {
    return isClockTime(candidate) ? candidate : fallback;
  }

  /** The `HH:MM` the two dropdowns display; always a value both selects can show. */
  const displayedTime = $derived(
    clockTimeOrFallback(isLive ? clock.value : store.value, clock.value),
  );
  const [selectedHour, selectedMinute] = $derived(displayedTime.split(':'));

  function pickNow(): void {
    store.setNow();
  }

  /** Pins the departure hour; `now` stops tracking. */
  function pinHour(hour: string): void {
    store.setFixed(`${hour}:${selectedMinute}`);
  }

  /** Pins the departure minute; `now` stops tracking. */
  function pinMinute(minute: string): void {
    store.setFixed(`${selectedHour}:${minute}`);
  }
</script>

<!-- Departure-time picker: hour and minute dropdowns plus a Now button. The option
     labels are rendered here, so the control reads 24h under every browser locale and
     iOS offers a native wheel. `bind:value` drives each select through its IDL value,
     which a user-picked option honours; an option's value is its label. -->
<div class="time-picker">
  <span class="tp-label">Depart:</span>
  <span class="tp-fields" class:tp-fields-live={isLive}>
    <select
      class="tp-select"
      bind:value={() => selectedHour, pinHour}
      use:clockOptions={HOURS_PER_DAY}
      aria-label="Departure hour"
      title="Hour (24-hour clock)"
    ></select>
    <span class="tp-colon">:</span>
    <select
      class="tp-select"
      bind:value={() => selectedMinute, pinMinute}
      use:clockOptions={MINUTES_PER_HOUR}
      aria-label="Departure minute"
      title="Minute"
    ></select>
  </span>
  <button
    type="button"
    class="tp-reset"
    class:tp-reset-active={isLive}
    onclick={pickNow}
    aria-pressed={isLive}>Now</button
  >
</div>

<style>
  .time-picker {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    margin-bottom: var(--space-md);
    font-size: var(--font-size-sm);
    color: var(--color-fg-muted);
  }

  .tp-fields {
    display: inline-flex;
    align-items: center;
    gap: var(--space-xxs);
  }

  .tp-fields-live .tp-select {
    color: var(--color-fg-muted);
    font-style: italic;
  }

  .tp-colon {
    color: var(--color-fg-muted);
    font-weight: 600;
  }

  .tp-select {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-fg);
    padding: var(--space-xs) 6px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    font-variant-numeric: tabular-nums;

    /* Safari shrinks selects to their widest option and adds its own chrome;
       a floor keeps both fields the same size regardless. */
    min-width: 56px;
  }

  .tp-select:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  .tp-reset {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-primary);
    padding: 4px 10px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  .tp-reset:hover {
    border-color: var(--color-primary);
  }

  .tp-reset-active {
    background: var(--color-primary);
    color: #000;
    border-color: var(--color-primary);
  }
</style>
